/**
 * Issue #136 — what a pinned place IS, on the client.
 *
 * ONE module, because the type has to read the same on every surface it
 * appears on: the three buttons in the add dialog, the pin glyph on the map,
 * the badge on a card, the popup, the picker rows, the near-me rows, the
 * correction form and the filter. Nine places is exactly how many chances
 * there are for "Nets" to become "Training nets" on one of them.
 *
 * IT IS DELIBERATELY NOT A FACILITIES LIST (see `api/src/db/schema.ts`):
 * one value per ground, never a set. If a future ticket needs "this club has
 * nets", that is `ground_facilities`, and it does not come through here.
 *
 * The values are the API's and the CHECK constraint's, character for
 * character. `PLACE_TYPES` is the order the UI presents them in, and `field`
 * is first because it is the commonest answer — NOT because it is the
 * default. There is no default on the write path; see `GroundSearchDialog`.
 */

export const PLACE_TYPES = ["field", "nets", "indoor"];

/**
 * `label` is what a person reads. `glyph` is the single character drawn
 * inside a map pin — a letter rather than an emoji or an icon because
 * `makeIcon` renders it at 11px inside a 34px rotated marker, where anything
 * with internal detail becomes a smudge. Colour on those pins already means
 * something else (whose ground it is, #57/#134's legend), so the glyph is
 * the dimension that was free to carry the type.
 *
 * `hint` is the one line under each button in the add dialog. It exists
 * because "nets" and "indoor" are the two a contributor can genuinely be
 * unsure between, and a wrong answer here is a wrong answer that other
 * people then read.
 */
export const PLACE_TYPE_META = {
  field: {
    label: "Field",
    glyph: "F",
    hint: "A ground you play matches on.",
  },
  nets: {
    label: "Nets",
    glyph: "N",
    hint: "Practice nets, in the open air.",
  },
  indoor: {
    label: "Indoor",
    glyph: "I",
    hint: "An indoor centre or hall.",
  },
};

/** The label, or `null` when there is nothing to say — never a guess. */
export function placeTypeLabel(value) {
  return PLACE_TYPE_META[value]?.label ?? null;
}

/**
 * The pin glyph. Falls back to `?` rather than to `F`: a ground whose type
 * the client cannot read is not a field, and drawing it as one would put a
 * claim on the map that no row supports. In practice this is unreachable —
 * `place_type` is NOT NULL — which is the point of it being visibly odd if
 * it ever happens.
 */
export function placeTypeGlyph(value) {
  return PLACE_TYPE_META[value]?.glyph ?? "?";
}

export function isPlaceType(value) {
  return PLACE_TYPES.includes(value);
}

/**
 * The filter's options, "all" first. Written out rather than derived from
 * `PLACE_TYPE_META` by suffixing an "s" — "Nets" pluralises to "Nets", not
 * "Netss", and "Indoor" is an adjective. Three lines beats a rule with two
 * exceptions in it.
 */
export const PLACE_TYPE_FILTERS = [
  { value: "all", label: "All types" },
  { value: "field", label: "Fields" },
  { value: "nets", label: "Nets" },
  { value: "indoor", label: "Indoor" },
];
